'use client';
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type FC,
  type MouseEvent,
  type ChangeEvent,
  useMemo,
  Fragment,
  Suspense,
} from 'react';
import {
  ChevronDoubleRightOutline,
  FormatSize,
  HDQuality,
  PodcastOutline,
  SettingsOutline,
} from '@eqi-investimentos/icons/web';
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from '@eqi-investimentos/react';
import ReactPlayer from 'react-player';
import useLocalStorage from '../../../hooks/useLocalstorage';
import { useTimeout } from '@/hooks/useTimeout';
import SkipContent from '@/components/sections/Player/Controls/SkipContent';
import { formatTime } from '@/utils/date';
import FullscreenButton from './Controls/Fullscreen';
import PlayPauseButton from './Controls/PlayPause';
import { Progress } from './Controls/Progress';
import SkipRewindButton from './Controls/SkipRewind';
import { Volume } from './Controls/Volume';
import PlayerError from './UI/Error';
import KeyAction, { KeyActionHandle } from './UI/KeyAction/KeyAction';
import { Loader } from './UI/Loader/Loader';

export const VideoPlayer: FC<VideoPlayerProps> = ({
  src,
  srcHD,
  authors,
  title,
  autoPlay = true,
  nextSlug,
  previousSlug,
  progress,
  hasAudioMode = false,
  hasTextMode = false,
  isContentPage = false,
  setAudioMode,
  setTextMode,
  sendWatchProgress,
}) => {
  const [displayControls, setDisplayControls] = useState(true);
  const [playbackState, setPlaybackState] = useState(false);
  const [volumeState, setVolumeState] = useLocalStorage('video-volume', 1);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [bufferProgress, setBufferProgress] = useState(0);
  const [seekProgress, setSeekProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [seekTooltip, setSeekTooltip] = useState('00:00');
  const [seekTooltipPosition, setSeekTooltipPosition] = useState('');
  const [currentTimeUI, setCurrentTimeUI] = useState('00:00');
  const [fullscreenState, setFullscreenState] = useState(false);
  const [hdState, setHdState] = useState(false);
  const [playbackRates] = useState([0.5, 0.75, 1, 1.25, 1.5, 2]);
  const [activePlaybackRate, setActivePlaybackRate] = useLocalStorage(
    'video-playbackrate',
    1
  );
  const [displayLoader, setDisplayLoader] = useState(true);
  const [volumeKeyAction, setVolumeKeyAction] = useState(false);
  const [videoError, setVideoError] = useState<MediaError | null>(null);

  const videoRef = useRef<ReactPlayer>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoKeyActionRef = useRef<KeyActionHandle>(null);

  const playPromise = useRef<Promise<void>>(null);
  const volumeData = useRef(volumeState || 1);
  const progressSeekData = useRef(0);

  const [setControlsTimeout] = useTimeout();
  const [setKeyActionVolumeTimeout] = useTimeout();
  const [setLoaderTimeout, clearLoaderTimeout] = useTimeout();

  /**
   * SEND WATCH PROGRESS
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const video = videoRef.current!;
      const internalPlayer = video.getInternalPlayer();

      if (!playbackState) return;

      sendWatchProgress?.(internalPlayer.currentTime, internalPlayer.duration);
    }, 10000);

    return () => clearInterval(interval);
  }, [playbackState, sendWatchProgress]);

  /**
   * TOGGLE SHOWING CONTROLS
   */

  const hideControlsHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    if (internalPlayer.paused) {
      return;
    }

    setDisplayControls(false);
  }, []);

  const showControlsHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    setDisplayControls(true);

    if (internalPlayer.paused) {
      return;
    }

    setControlsTimeout(() => {
      hideControlsHandler();
    }, 2000);
  }, [hideControlsHandler, setControlsTimeout]);

  /**
   * PLAYBACK
   */

  const togglePlayHandler = useCallback(() => {
    const video = videoRef.current!;

    const internalPlayer = video.getInternalPlayer();

    if (internalPlayer.paused || internalPlayer.ended) {
      playPromise.current = internalPlayer.play();
      return;
    }

    if (!playPromise.current) {
      return;
    }

    playPromise.current?.then(() => {
      internalPlayer.pause();
    });
  }, []);

  const videoPlayHandler = useCallback(() => {
    setPlaybackState(true);
    showControlsHandler();
  }, [showControlsHandler]);

  const videoPauseHandler = useCallback(() => {
    setPlaybackState(false);
    showControlsHandler();
  }, [showControlsHandler]);

  /**
   * LOADING
   */

  const showLoaderHandler = useCallback(() => {
    setLoaderTimeout(() => setDisplayLoader(true), 300);
  }, [setLoaderTimeout]);

  const hideLoaderHandler = useCallback(() => {
    clearLoaderTimeout();
    setDisplayLoader(false);
  }, [clearLoaderTimeout]);

  /**
   * VOLUME
   */

  const volumeInputHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current!;
      const internalPlayer = video.getInternalPlayer();

      internalPlayer.volume = +event.target.value;
    },
    []
  );

  const volumeChangeHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    setVolumeState(internalPlayer.volume);

    if (internalPlayer.volume === 0) {
      internalPlayer.muted = true;
    } else {
      internalPlayer.muted = false;
      volumeData.current = internalPlayer.volume;
    }
  }, [setVolumeState]);

  const toggleMuteHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    if (internalPlayer.volume !== 0) {
      volumeData.current = internalPlayer.volume;
      internalPlayer.volume = 0;
      setVolumeState(0);
    } else {
      internalPlayer.volume = volumeData.current;
      setVolumeState(volumeData.current);
    }
  }, [setVolumeState]);

  /**
   * TIME
   */

  const timeChangeHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    const duration = internalPlayer.duration || 0;
    const currentTime = internalPlayer.currentTime || 0;
    const buffer = internalPlayer.buffered;

    // Progress
    setCurrentProgress((currentTime / duration) * 100);
    setSeekProgress(currentTime);

    if (duration > 0) {
      for (let i = 0; i < buffer.length; i++) {
        if (
          buffer.start(buffer.length - 1 - i) === 0 ||
          buffer.start(buffer.length - 1 - i) < internalPlayer.currentTime
        ) {
          setBufferProgress(
            (buffer.end(buffer.length - 1 - i) / duration) * 100
          );
          break;
        }
      }
    }

    // Time
    const formattedCurrentTime = formatTime(Math.round(currentTime));
    setCurrentTimeUI(formattedCurrentTime);
  }, []);

  /**
   * SEEK
   */

  const seekMouseMoveHandler = useCallback((event: MouseEvent) => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    const rect = event.currentTarget.getBoundingClientRect();
    const skipTo =
      (event.nativeEvent.offsetX / rect.width) * internalPlayer.duration;

    progressSeekData.current = skipTo;

    let formattedTime: string;

    if (skipTo > internalPlayer.duration) {
      formattedTime = formatTime(internalPlayer.duration);
    } else if (skipTo < 0) {
      formattedTime = '00:00';
    } else {
      formattedTime = formatTime(skipTo);
      setSeekTooltipPosition(`${event.nativeEvent.offsetX}px`);
    }

    setSeekTooltip(formattedTime);
  }, []);

  const seekInputHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current!;
      const internalPlayer = video.getInternalPlayer();

      const skipTo = progressSeekData.current || +event.target.value;

      internalPlayer.currentTime = skipTo;
      setCurrentProgress((skipTo / internalPlayer.duration) * 100);
      setSeekProgress(skipTo);
    },
    []
  );

  /**
   * REWIND & SKIP
   */

  const rewindHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    internalPlayer.currentTime -= 10;

    const rewindContainer = videoKeyActionRef.current!.rewind;
    const rewindElement = rewindContainer.firstElementChild as HTMLElement;

    rewindContainer.animate(
      [{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }],
      {
        duration: 1000,
        easing: 'ease-out',
        fill: 'forwards',
      }
    );
    rewindElement.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(-20%)` },
      ],
      {
        duration: 1000,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );
  }, []);

  const skipHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    internalPlayer.currentTime += 10;

    const forwardContainer = videoKeyActionRef.current!.skip;
    const forwardElement = forwardContainer.firstElementChild as HTMLElement;

    forwardContainer.animate(
      [{ opacity: 0 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }],
      {
        duration: 1000,
        easing: 'ease-out',
        fill: 'forwards',
      }
    );
    forwardElement.animate(
      [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(20%)` },
      ],
      {
        duration: 1000,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );
  }, []);

  /**
   * FULLSCREEN
   */

  const toggleFullscreenHandler = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      videoContainerRef.current!.requestFullscreen();
    }
  }, []);

  const fullscreenChangeHandler = useCallback(() => {
    if (document.fullscreenElement) {
      setFullscreenState(true);
    } else {
      setFullscreenState(false);
    }
  }, []);

  /**
   * SETTINGS
   */

  const togglePlaybackRateHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    const currentPlayBackRate = internalPlayer.playbackRate;

    const currentIndex = playbackRates.indexOf(currentPlayBackRate);
    const nextIndex =
      currentIndex + 1 === playbackRates.length ? 0 : currentIndex + 1;

    const playbackRate = playbackRates[nextIndex];

    internalPlayer.playbackRate = playbackRate;
    setActivePlaybackRate(playbackRate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleHdHandler = useCallback(() => {
    if (!srcHD) {
      return;
    }

    showLoaderHandler();
    setHdState((prevState) => !prevState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * KEYBOARD SHORTKUTS
   */

  const keyEventHandler = useCallback(
    (event: KeyboardEvent) => {
      const video = videoRef.current!;
      const internalPlayer = video.getInternalPlayer();

      const activeElement = document.activeElement;

      if (
        !activeElement ||
        (activeElement.localName === 'input' &&
          (activeElement as HTMLInputElement).type !== 'range') ||
        activeElement.localName === 'textarea'
      ) {
        return;
      }

      const { key } = event;

      switch (key) {
        case 'ArrowLeft':
          event.preventDefault();
          rewindHandler();
          break;
        case 'ArrowRight':
          event.preventDefault();
          skipHandler();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (internalPlayer.volume + 0.05 > 1) {
            internalPlayer.volume = 1;
          } else {
            internalPlayer.volume = +(internalPlayer.volume + 0.05).toFixed(2);
          }

          setVolumeKeyAction(true);
          setKeyActionVolumeTimeout(() => {
            setVolumeKeyAction(false);
          }, 1500);

          break;
        case 'ArrowDown':
          event.preventDefault();
          if (internalPlayer.volume - 0.05 < 0) {
            internalPlayer.volume = 0;
          } else {
            internalPlayer.volume = +(internalPlayer.volume - 0.05).toFixed(2);
          }

          setVolumeKeyAction(true);
          setKeyActionVolumeTimeout(() => {
            setVolumeKeyAction(false);
          }, 1500);

          break;
        case ' ':
          event.preventDefault();
          togglePlayHandler();
          break;
      }
    },
    [togglePlayHandler, rewindHandler, skipHandler, setKeyActionVolumeTimeout]
  );

  /**
   * LOAD VIDEO
   */

  const videoLoadedHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    internalPlayer.volume = volumeState;
    internalPlayer.playbackRate = activePlaybackRate;

    setVideoDuration(internalPlayer.duration);
    timeChangeHandler();

    document.addEventListener('keydown', keyEventHandler);
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);

    if (progress) {
      internalPlayer.currentTime = progress;
    }

    if (autoPlay) playPromise.current = internalPlayer.play();
  }, [
    autoPlay,
    volumeState,
    activePlaybackRate,
    timeChangeHandler,
    keyEventHandler,
    fullscreenChangeHandler,
    progress,
  ]);

  /**
   * ERROR HANDLER
   */

  const errorHandler = useCallback(() => {
    const video = videoRef.current!;
    const internalPlayer = video.getInternalPlayer();

    if (internalPlayer.error) {
      console.error(internalPlayer.error);
      setVideoError(internalPlayer.error);
    }
  }, []);

  /**
   * INITIATE PLAYER
   */

  useEffect(() => {
    return () => {
      document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      document.removeEventListener('keydown', keyEventHandler);
    };
  }, [fullscreenChangeHandler, keyEventHandler]);

  /**
   * RENDER
   */

  const settings = useMemo(
    () => [
      {
        icon: <ChevronDoubleRightOutline />,
        description: 'Velocidade',
        value: activePlaybackRate,
        onClick: togglePlaybackRateHandler,
      },
      {
        icon: <HDQuality />,
        description: 'Qualidade',
        value: hdState ? 'HD' : 'Automático',
        onClick: toggleHdHandler,
        hide: !srcHD,
      },
      {
        icon: <PodcastOutline />,
        description: 'Ir para modo Podcast',
        onClick: setAudioMode,
        hide: !hasAudioMode,
      },
      {
        icon: <FormatSize />,
        description: 'Ir para modo Texto',
        onClick: setTextMode,
        hide: !hasTextMode,
      },
    ],
    [
      activePlaybackRate,
      hasAudioMode,
      hasTextMode,
      hdState,
      setAudioMode,
      setTextMode,
      srcHD,
      toggleHdHandler,
      togglePlaybackRateHandler,
    ]
  );

  return (
    <Flex
      ref={videoContainerRef}
      cursor={displayControls ? 'default' : 'none'}
      bg="background.alwaysDark"
      position="relative"
      isolation="isolate"
      w={isContentPage ? '100vw' : 'full'}
      h={isContentPage ? '32rem' : 'full'}
      color="content.alwaysLight"
      borderRadius="xs"
      onMouseMove={showControlsHandler}
      onMouseLeave={hideControlsHandler}
      sx={{
        '& video': {
          w: 'full',
          h: 'full',
          zIndex: 1,
        },
      }}
    >
      <Suspense>
        <ReactPlayer
          ref={videoRef}
          url={hdState && srcHD ? srcHD : src}
          controls={false}
          onLoadedMetadata={videoLoadedHandler}
          onClick={togglePlayHandler}
          onPlay={videoPlayHandler}
          onPause={videoPauseHandler}
          onVolumeChange={volumeChangeHandler}
          onTimeUpdate={timeChangeHandler}
          onDoubleClick={toggleFullscreenHandler}
          onSeeking={showLoaderHandler}
          onSeeked={hideLoaderHandler}
          onWaiting={showLoaderHandler}
          onCanPlay={hideLoaderHandler}
          onError={errorHandler}
          width="100%"
          height="100%"
          tracks={[
            {
              kind: 'captions',
              src: '',
              srcLang: 'pt-BR',
              label: 'No captions available',
            },
          ]}
        />
      </Suspense>

      <Loader on={displayLoader} />
      <KeyAction
        ref={videoKeyActionRef}
        on={volumeKeyAction}
        volume={volumeState}
        onClick={togglePlayHandler}
      />
      <PlayerError error={videoError} />
      <Flex
        opacity={displayControls ? 1 : 0}
        position="absolute"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        h="full"
        w="full"
        bg="rgba(18, 19, 24, 0.8)"
        transition="opacity 300ms ease-out"
        zIndex="15"
      >
        <Flex p="2.75rem" pb="0" w="full" justifyContent="space-between">
          <Flex direction="column" gap="2xs">
            <Text size="h6">{title}</Text>
            <Text size="xl" color="content.brandLight">
              {authors}
            </Text>
          </Flex>
          <Menu>
            <MenuButton h="fit-content">
              <SettingsOutline boxSize="2.5rem" />
            </MenuButton>
            <MenuList
              flexDirection="column"
              p="xl"
              bg="background.alwaysDark"
              gap="xl"
              w="20rem"
            >
              <Text size="xl">Configurações</Text>
              <Flex paddingTop="xl" direction="column" gap="m">
                {settings.map(
                  ({ hide, description, icon, value, onClick }, idx) =>
                    !hide && (
                      <Fragment key={description}>
                        {idx > 0 && <MenuDivider />}
                        <MenuItem
                          as={Button}
                          variant="naked"
                          p="0"
                          gap="s"
                          justifyContent="flex-start"
                          leftIcon={icon}
                          onClick={onClick}
                          backgroundColor="content.alwaysDark"
                        >
                          <Text size="l">
                            {`${description}${value !== undefined ? ' • ' + value : ''}`}
                          </Text>
                        </MenuItem>
                      </Fragment>
                    )
                )}
              </Flex>
            </MenuList>
          </Menu>
        </Flex>
        <Flex
          gap="6xl"
          alignItems="center"
          sx={{ '& svg': { w: '3rem', h: '3rem' } }}
        >
          {!!previousSlug && <SkipContent slug={previousSlug} type="prev" />}
          <SkipRewindButton type="rewind" handleClick={rewindHandler} />
          <PlayPauseButton
            isPlaying={playbackState}
            onToggle={togglePlayHandler}
          />
          <SkipRewindButton type="skip" handleClick={skipHandler} />
          {!!nextSlug && <SkipContent slug={nextSlug} type="next" />}
        </Flex>
        <Flex direction="column" gap="m" w="full">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            w="full"
            px="2.75rem"
          >
            <Flex gap="s" alignItems="center">
              <Volume
                volume={volumeState}
                onToggle={toggleMuteHandler}
                onSeek={volumeInputHandler}
              />
              <Text size="l">
                {`${currentTimeUI} / ${formatTime(videoDuration)}`}
              </Text>
            </Flex>
            <FullscreenButton
              isFullscreen={fullscreenState}
              onToggle={toggleFullscreenHandler}
            />
          </Flex>
          <Flex w="full" py="xs">
            <Progress
              bufferProgress={bufferProgress}
              currentProgress={currentProgress}
              videoDuration={videoDuration}
              seekProgress={seekProgress}
              seekTooltip={seekTooltip}
              seekTooltipPosition={seekTooltipPosition}
              onHover={seekMouseMoveHandler}
              onSeek={seekInputHandler}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export type VideoPlayerProps = {
  src: string;
  srcHD: string | null;
  authors?: string;
  title: string;
  autoPlay?: boolean;
  nextSlug?: string;
  previousSlug?: string;
  progress?: number;
  hasAudioMode?: boolean;
  hasTextMode?: boolean;
  isContentPage?: boolean;
  setAudioMode?: () => void;
  setTextMode?: () => void;
  sendWatchProgress?: (progress: number, duration: number) => void;
};
